import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { accountSelector } from 'store/account/account.selector';
import { useAppSelector } from 'store/hooks';
import { ProgramsMapping } from 'data/index';
import { IFrameView } from 'components/fragment';
import { jwtSelector } from 'store/auth/auth.selector';
import { programsListWSUpdatesSelector } from 'store/app/app.selector';

type ProgramContainerViewProps = {
  title: string;
  programId: string;
  isSplit?: boolean;
  onProgramRemove: ((programId: string) => void) | undefined;
  onProgramFullScreen: ((programId: string) => boolean) | undefined;
  isFullScreen?: boolean;
  highlightedDate: string;
  isHidden?: boolean;
};

const ReturnProgram: React.FC<{
  programId: string;
  account: IUserPortal | undefined;
  token: string | undefined;
  isFullScreen: boolean | undefined;
  isAdmin: boolean | undefined;
  highlightedDate: string;
}> = memo(({ programId, account, token, isAdmin, isFullScreen, highlightedDate }) => {
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 1200,
    height: 720,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      // Debounce resize handling to prevent excessive re-renders
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        const { width, height } = entries[0].contentRect;
        // Only update if dimensions actually changed
        setDimensions((prev) => {
          if (prev.width === width && prev.height === height) {
            return prev;
          }
          return { width, height };
        });
      }, 100);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const openDisplayView = (app: AppNameDisplayViewTypes) => {
    if (!token) {
      return console.error('No token found');
    }
    const url: string = window.location.origin + '/display-view/' + app + '?id_token=' + token;

    window.open(url, '_blank');
  };

  const openPrintView = (id: number, app: AppNameDisplayViewTypes) => {
    if (!token) {
      return console.error('No token found');
    }
    if (id === undefined) {
      return console.error('No ID provided');
    }
    const url: string =
      window.location.origin + '/print-view/' + app + '?id_token=' + token + '&id=' + id;

    window.open(url, '_blank');
  };

  if (account === undefined) {
    return <div>No account found</div>;
  } else {
    if (Object.keys(ProgramsMapping).includes(programId)) {
      const Program = ProgramsMapping[programId];

      const programAppMap: Record<string, AppNameDisplayViewTypes> = {
        '1': 'sigevents',
        '3': 'resourcerequest',
        '8': 'positionlogs',
        '18': 'battlerhythm',
        '21': 'operationalrhythm',
      };

      const appName = programId ? programAppMap[programId] : null;

      return (
        <div ref={containerRef} className='w-full h-full overflow-hidden bg-white shadow-xl'>
          <Program
            account={account}
            userAuth={{ jwt: token }}
            isFullScreen={isFullScreen}
            isAdmin={isAdmin}
            highlightedDate={highlightedDate}
            dimensions={dimensions}
            cbDisplayView={appName ? () => openDisplayView(appName) : undefined}
            cbPrintView={appName ? (id: number) => openPrintView(id, appName) : undefined}
          />
        </div>
      );
    } else {
      return <IFrameView account={account} programId={programId} />;
    }
  }
});

const ProgramContainerView: React.FC<ProgramContainerViewProps> = ({
  programId,
  isFullScreen,
  highlightedDate,
  isHidden,
}) => {
  const [componentKey, setComponentKey] = useState(0);
  const account = useAppSelector(accountSelector) || undefined;
  const token = useAppSelector(jwtSelector);
  const userProgramsListWSUpdates = useAppSelector(programsListWSUpdatesSelector);
  const previousProgramsListRef = useRef<number[]>([]);

  const remount = useCallback(() => {
    setComponentKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    const programIdNum = Number(programId);

    const thisProgramUpdated =
      userProgramsListWSUpdates.includes(programIdNum) &&
      !previousProgramsListRef.current.includes(programIdNum);

    if (thisProgramUpdated) {
      remount();
    }

    previousProgramsListRef.current = [...userProgramsListWSUpdates];
  }, [userProgramsListWSUpdates, programId, remount]);

  return (
    <div
      className={`flex flex-col items-center justify-center w-full h-[calc(100%-32px)] relative rounded-md overflow-hidden`}>
      <div className='flex flex-col items-center w-full h-[calc(100%)] bg-lynch-100' style={{ position: 'absolute', top: 0, left: 0 }}>
        <ReturnProgram
          programId={programId}
          account={account}
          token={token}
          isFullScreen={isFullScreen}
          isAdmin={Boolean(account?.pvAdministrator)}
          highlightedDate={highlightedDate}
          key={componentKey}
        />
      </div>
    </div>
  );
};

// Custom comparison: skip re-renders when component is hidden
const arePropsEqual = (
  prevProps: ProgramContainerViewProps,
  nextProps: ProgramContainerViewProps
) => {
  // If component is hidden, skip re-render unless it's becoming visible
  if (nextProps.isHidden && prevProps.isHidden) {
    return true; // Both hidden, no need to re-render
  }
  // Otherwise, do shallow comparison
  return (
    prevProps.programId === nextProps.programId &&
    prevProps.isFullScreen === nextProps.isFullScreen &&
    prevProps.highlightedDate === nextProps.highlightedDate &&
    prevProps.isHidden === nextProps.isHidden &&
    prevProps.isSplit === nextProps.isSplit
  );
};

export default memo(ProgramContainerView, arePropsEqual);
